import * as React from 'react';
import * as ReactQuery from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { MultiSelect } from 'primereact/multiselect';
import { Toast } from 'primereact/toast';
import { StatusCodes } from 'http-status-codes';
import {
  listAllDevicesRequest,
  queueYDeviceDownlinkRequest,
} from '../apis/calyxSaaSDashboardAPI';
import ByteUtils from '../utils/ByteUtils';
import { makeOption, validateArg } from '../utils/downlinkArgUtils';

/** @typedef {import('../utils/downlinkArgUtils').Arg} Arg */

const sensorTypeOptions = [
  { name: 'SENSOR_TYPE_CALYX_NH3 (0x01)', value: 0x01 },
  { name: 'SENSOR_TYPE_EC_NH3 (0x02)', value: 0x02 },
  { name: 'SENSOR_TYPE_MULBERRY_CO2 (0x03)', value: 0x03 },
  { name: 'SENSOR_TYPE_SHT30 (0x04)', value: 0x04 },
  { name: 'SENSOR_TYPE_SCD4X_CO2 (0x05)', value: 0x05 },
  { name: 'SENSOR_TYPE_CYCFS1 (0x06)', value: 0x06 },
  { name: 'ULTRASONIC_ANEMOMETER (0x07)', value: 0x07 },
  { name: 'SENSOR_TYPE_MULBERRY_CH4 (0x08)', value: 0x08 },
  { name: 'SENSOR_INVALID_INDEX (0xff)', value: 0xff },
];

/**
  @type {{
    name: string;
    args?: Arg[];
    payload: (args: Record<string, any>) => Uint8Array<ArrayBuffer>;
  }[]}
**/
const availableDownlinks = [
  {
    name: '[Type 5] Request System Information',
    payload: (args) => {
      const { u8 } = ByteUtils;

      const payloadArr = [
        ...u8(5),
      ];

      return Uint8Array.from(payloadArr);
    },
  },
  {
    name: '[Type 6] Request System Parameter',
    payload: (args) => {
      const { u8 } = ByteUtils;

      const payloadArr = [
        ...u8(6),
      ];

      return Uint8Array.from(payloadArr);
    },
  },
  {
    name: '[Type 11] Adjust Interval',
    args: [
      {
        key: 'regular_measurement_interval',
        name: 'Regular Measurement Interval (min.)',
        type: 'u8',
      },
      {
        key: 'status_report_interval',
        name: 'Status Report Interval (min.)',
        type: 'u16',
      },
      {
        key: 'sleep_n1',
        name: 'Sleep N1 (Probe B Multiplier)',
        type: 'u8',
        default: 1,
        halfWidth: true,
      },
      {
        key: 'sleep_n2',
        name: 'Sleep N2 (Probe C Multiplier)',
        type: 'u8',
        default: 1,
        halfWidth: true,
      },
    ],
    payload: (args) => {
      const { u8, u16 } = ByteUtils;

      const payloadArr = [
        ...u8(11),
        ...u8(args.regular_measurement_interval),
        ...u16(args.status_report_interval),
        ...u8(args.sleep_n1),
        ...u8(args.sleep_n2),
      ];

      return Uint8Array.from(payloadArr);
    },
  },
  {
    name: '[Type 12] Adjust NH3 Threshold',
    args: [
      {
        key: 'nh3_threshold',
        name: 'NH3 Threshold (ppm)',
        type: 'u8',
      },
    ],
    payload: (args) => {
      const { u8 } = ByteUtils;

      const payloadArr = [
        ...u8(12),
        ...u8(args.nh3_threshold),
      ];

      return Uint8Array.from(payloadArr);
    },
  },
  {
    name: '[Type 13] Adjust Temperature Threshold',
    args: [
      {
        key: 'temperature_threshold',
        name: 'Temperature Threshold (0.1 °C)',
        type: 'u8',
      },
    ],
    payload: (args) => {
      const { u8 } = ByteUtils;

      const payloadArr = [
        ...u8(13),
        ...u8(args.temperature_threshold),
      ];

      return Uint8Array.from(payloadArr);
    },
  },
  {
    name: '[Type 14] Adjust CO2 Threshold',
    args: [
      {
        key: 'co2_threshold',
        name: 'CO2 Threshold (10 ppm)',
        type: 'u8',
      },
    ],
    payload: (args) => {
      const { u8 } = ByteUtils;

      const payloadArr = [
        ...u8(14),
        ...u8(args.co2_threshold),
      ];

      return Uint8Array.from(payloadArr);
    },
  },
  {
    name: '[Type 15] Adjust Anemometer Threshold',
    args: [
      {
        key: 'anemometer_threshold',
        name: 'Anemometer Threshold (0.1 m/s)',
        type: 'u8',
      },
    ],
    payload: (args) => {
      const { u8 } = ByteUtils;

      const payloadArr = [
        ...u8(15),
        ...u8(args.anemometer_threshold),
      ];

      return Uint8Array.from(payloadArr);
    },
  },
  {
    name: "[Type 16] Adjust Sensor Type Parameter",
    args: [
      {
        key: 'sensor_a_type',
        name: 'Sensor A Type',
        type: 'option',
        options: sensorTypeOptions,
      },
      {
        key: 'sensor_b_type',
        name: 'Sensor B Type',
        type: 'option',
        options: sensorTypeOptions,
      },
      {
        key: 'sensor_c_type',
        name: 'Sensor C Type',
        type: 'option',
        options: sensorTypeOptions,
      },
    ],
    payload: (args) => {
      const { u8 } = ByteUtils;

      const payloadArr = [
        ...u8(16),
        ...u8(args.sensor_a_type),
        ...u8(args.sensor_b_type),
        ...u8(args.sensor_c_type),
      ];

      return Uint8Array.from(payloadArr);
    },
  },
  {
    name: "[Type 17] Adjust Sensor Value Resource",
    args: [
      {
        key: 'customer',
        name: 'Customer',
        type: 'option',
        options: [
          {
            name: 'Default (0x00)',
            value: 0x00,
          },
          {
            name: 'Customer (0x01)',
            value: 0x01,
          },
        ],
      },
      {
        key: 'temperature',
        name: 'Temperature',
        type: 'option',
        options: sensorTypeOptions,
        default: 0xff,
      },
      {
        key: 'humidity',
        name: 'Humidity',
        type: 'option',
        options: sensorTypeOptions,
        default: 0xff,
      },
      {
        key: 'nh3',
        name: 'NH3',
        type: 'option',
        options: sensorTypeOptions,
        default: 0xff,
      },
      {
        key: 'co2',
        name: 'CO2',
        type: 'option',
        options: sensorTypeOptions,
        default: 0xff,
      },
      {
        key: 'wind_speed',
        name: 'Wind Speed',
        type: 'option',
        options: sensorTypeOptions,
        default: 0xff,
      },
      {
        key: 'ch4',
        name: 'CH4',
        type: 'option',
        options: sensorTypeOptions,
        default: 0xff,
      },
    ],
    payload: (args) => {
      const { u8 } = ByteUtils;

      const payloadArr = [
        ...u8(17),
        ...u8(args.customer),
        ...u8(args.temperature),
        ...u8(args.humidity),
        ...u8(args.nh3),
        ...u8(args.co2),
        ...u8(args.wind_speed),
        ...u8(args.ch4),
      ];

      return Uint8Array.from(payloadArr);
    },
  },
  {
    name: '[Type 18] Adjust CH4 Threshold',
    args: [
      {
        key: 'ch4_threshold',
        name: 'CH4 Threshold (10 ppm)',
        type: 'u8',
      },
    ],
    payload: (args) => {
      const { u8 } = ByteUtils;

      const payloadArr = [
        ...u8(18),
        ...u8(args.ch4_threshold),
      ];

      return Uint8Array.from(payloadArr);
    },
  },
  {
    name: '[Type 19] Adjust Sensor Offset',
    args: [
      {
        key: 'sensor_type',
        name: 'Sensor Type',
        type: 'option',
        options: sensorTypeOptions,
      },
      {
        key: 'attribute',
        name: 'Attribute',
        type: 'option',
        options: [
          { name: 'Temperature (0x00)', value: 0x00 },
          { name: 'Humidity (0x01)', value: 0x01 },
          { name: 'NH3 (0x02)', value: 0x02 },
          { name: 'CO2 (0x03)', value: 0x03 },
          { name: 'Wind Speed (0x04)', value: 0x04 },
          { name: 'Wind Direction (0x05)', value: 0x05 },
          { name: 'CH4 (0x06)', value: 0x06 },
        ],
      },
      {
        key: 'offset_value',
        name: 'Offset Value',
        type: 'number',
      },
    ],
    payload: (args) => {
      const { u8, f32 } = ByteUtils;

      const payloadArr = [
        ...u8(19),
        ...u8(args.sensor_type),
        ...u8(args.attribute),
        ...f32(args.offset_value),
        // insert checksum here
      ];

      const checksum = u8(payloadArr.reduce((acc, e) => (acc + e) % 256, 0));
      payloadArr.push(...checksum);

      return Uint8Array.from(payloadArr);
    },
  },
  {
    name: 'Raw Base64',
    args: [
      {
        key: 'base64_payload',
        name: 'Base64 Payload',
        type: 'string',
        validate: (value) => {
          try {
            Uint8Array.fromBase64(value);
            return true;
          } catch (err) {
            return 'Invalid base64 input.';
          }
        }
      },
    ],
    payload: (args) => {
      return Uint8Array.fromBase64(args.base64_payload);
    },
  },
];

export const DeviceDownlinkButton = (props) => {
  const allDevicesQuery = ReactQuery.useQuery(
    ['list', 'devices'],
    () => listAllDevicesRequest(),
  );

  const toast = React.useRef(
    /** @type {Toast | null} */ (null)
  );
  const [isDownlinkDialogVisible, setDownlinkDialogVisible] = React.useState(
    /** @type {boolean} */ (false)
  );
  const [selectedSerialNumbers, setSelectedSerialNumbers] = React.useState(
    /** @type {string[]} */ ([])
  );
  const [selectedDownlink, setSelectedDownlink] = React.useState(
    /** @type {typeof availableDownlinks[number] | null} */ (null)
  );
  const [selectedDownlinkArgs, setSelectedDownlinkArgs] = React.useState(
    /** @type {Record<string, any>} */ ({})
  );
  const [showResponseQueueResult, setShowResponseQueueResult] = React.useState(
    /** @type {boolean} */ (false)
  );

  // set default downlink args on downlink change
  React.useEffect(() => {
    setSelectedDownlinkArgs(
      Object.fromEntries((selectedDownlink?.args ?? [])
        .map((arg) => [arg.key, arg.default ?? undefined]))
    );
  }, [selectedDownlink]);

  const availableDeviceOptions = React.useMemo(
    () => (allDevicesQuery.data ?? []).map((device) => ({
      label: `${device.serialNumber} [${device.hardwareSerial}]`,
      value: device.serialNumber,
      serialNumber: device.serialNumber,
      hardwareSerial: device.hardwareSerial,
      disabled: !/^[0-9A-F]{16}$/.test(device.hardwareSerial),
    })),
    [allDevicesQuery.data],
  );
  const availableSerialNumbers = React.useMemo(
    () => /** @type {string[]} */ ((allDevicesQuery.data ?? []).map((device) => device.serialNumber)),
    [allDevicesQuery.data],
  );
  const availableSerialNumbersSet = React.useMemo(
    () => new Set(availableSerialNumbers),
    [availableSerialNumbers]
  );
  const isDownlinkValid = React.useMemo(
    () => {
      if (selectedDownlink == null) return false;
      if ((selectedDownlink.args ?? []).some((arg) => validateArg(arg, selectedDownlinkArgs[arg.key]) !== true)) return false;
      return true;
    },
    [selectedDownlink, selectedDownlinkArgs],
  );
  const currentPayload = React.useMemo(
    () => isDownlinkValid ? selectedDownlink?.payload(selectedDownlinkArgs) : null,
    [isDownlinkValid, selectedDownlink, selectedDownlinkArgs],
  );

  async function sendDownlinkToDevice(/** @type {string} */ selectedSerialNumber) {
    if (currentPayload == null) return;

    try {
      const { awsDeviceId, awsMessageId } = await queueYDeviceDownlinkRequest({
        serialNumber: selectedSerialNumber,
        payload: currentPayload.toBase64(),
      });

      toast.current.show({
        severity: 'success',
        summary: `[${selectedSerialNumber}] Downlink Success`,
        detail: `The downlink is successfully queued for ${selectedSerialNumber}.`,
      });

      if (showResponseQueueResult){
        toast.current.show({
          severity: 'info',
          summary: `[${selectedSerialNumber}] Queue Result`,
          detail: (
            <ul>
              <li>
                <b className="block">AWS Device Id</b>
                <code className="block py-1">{awsDeviceId}</code>
              </li>
              <li>
                <b className="block">AWS Message Id</b>
                <code className="block py-1">{awsMessageId}</code>
              </li>
            </ul>
          ),
          sticky: true,
        });
      }
    } catch (err) {
      if (err?.response?.status === StatusCodes.UNPROCESSABLE_ENTITY) {
        toast.current.show({
          severity: 'error',
          summary: `[${selectedSerialNumber}] Unable to send downlink for this device to AWS IoT`,
          detail: 'The device may not be register on AWS IoT yet.',
          sticky: true,
        });
      } else {
        toast.current.show({
          severity: 'error',
          summary: `[${selectedSerialNumber}] Unexpected Error`,
          detail: err.toString(),
          sticky: true,
        });
      }
    }
  }

  const sendDownlinksMutation = ReactQuery.useMutation({
    mutationFn: async () => {
      await Promise.allSettled(
        selectedSerialNumbers.map((selectedSerialNumber) => sendDownlinkToDevice(selectedSerialNumber)),
      );
    },
  });

  function editDeviceListInCSV() {
    const csv = window.prompt('CSV:', selectedSerialNumbers.join(','));

    if (csv == null) return;

    const serialNumbers = csv.split(',')
      .map((e) => e.trim())
      .filter((e) => e !== '');

    setSelectedSerialNumbers(serialNumbers);
  }

  async function handleSendDownlinks() {
    if (!isDownlinkValid) return;

    sendDownlinksMutation.mutate();
  }

  return (
    <>
      <Toast ref={toast} />

      <Button
        label='Send Downlink to Device'
        icon='pi pi-arrow-down'
        className='p-button-primary'
        onClick={() => setDownlinkDialogVisible(true)}
      />

      <Dialog
        visible={isDownlinkDialogVisible}
        header="Create Device Downlink"
        footer={
          <div>
            <Button
              className="p-button-primary"
              label="Send Downlink"
              icon="pi pi-arrow-down"
              loading={sendDownlinksMutation.isLoading}
              disabled={selectedSerialNumbers.length === 0 || !isDownlinkValid}
              onClick={handleSendDownlinks}
            />
          </div>
        }
        style={{ width: '550px' }}
        blockScroll
        onHide={() => setDownlinkDialogVisible(false)}
      >
        <div className="flex flex-column gap-3">
          <div>
            <label className="block mb-2">Serial Number(s)</label>
            <div className="flex gap-3">
              <MultiSelect
                value={selectedSerialNumbers}
                options={availableDeviceOptions}
                filterBy="serialNumber,hardwareSerial"
                display="chip"
                filter
                showSelectAll={false}
                placeholder="Select Serial Number(s)"
                invalid={selectedSerialNumbers.length === 0}
                className="w-full"
                pt={{
                  label: {
                    style: {
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '4px',
                    },
                  },
                  token: ({ context }) => ({
                    style: {
                      margin: '0',
                      fontFamily: 'monospace',
                      color: !availableSerialNumbersSet.has(context.value) ? 'red' : undefined,
                    },
                  }),
                  item: {
                    style: {
                      fontFamily: 'monospace',
                    },
                  },
                }}
                onChange={(event) => setSelectedSerialNumbers(event.value.toSorted())}
              />
              <Button
                className="flex-none"
                icon="pi pi-align-left"
                outlined
                tooltip="Edit in CSV"
                onClick={editDeviceListInCSV}
              />
            </div>
          </div>
          <div>
            <label className="block mb-2">Downlink Type</label>
            <Dropdown
              value={selectedDownlink}
              options={availableDownlinks}
              optionLabel="name"
              placeholder="Select a Downlink Type"
              invalid={selectedDownlink == null}
              className="w-full"
              onChange={(event) => setSelectedDownlink(event.value)}
            />
          </div>
          {(selectedDownlink?.args?.length ?? 0) > 0 ? (
            <div className="grid">
              {selectedDownlink?.args?.map((arg) => makeOption({
                arg,
                argValue: selectedDownlinkArgs[arg.key],
                setArgValue: (value) => {
                  setSelectedDownlinkArgs((prev) => ({ ...prev, [arg.key]: value }));
                },
              }))}
            </div>
          ) : null}
          <div>
            <label className="block font-medium text-lg mb-2">Payload Preview</label>
            <div className="flex flex-column gap-2">
              <div>
                <label className="block text-md mb-2">Unsigned Bytes</label>
                <InputTextarea
                  value={currentPayload?.join(', ') ?? ''}
                  rows={2}
                  spellCheck={false}
                  disabled
                  className="w-full"
                  style={{ fontFamily: 'monospace', tabSize: '4' }}
                />
              </div>
              <div>
                <label className="block text-md mb-2">Base64 Encoded</label>
                <InputTextarea
                  value={currentPayload?.toBase64() ?? ''}
                  rows={2}
                  spellCheck={false}
                  disabled
                  className="w-full"
                  style={{ fontFamily: 'monospace', tabSize: '4' }}
                />
              </div>
            </div>
          </div>
          <div>
            <label className="flex align-items-center gap-2">
              <Checkbox
                checked={showResponseQueueResult}
                onChange={(event) => setShowResponseQueueResult(event.checked ?? false)}
              />
              <span>Show response queue result</span>
            </label>
          </div>
        </div>
      </Dialog>
    </>
  );
};
